import {
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    InputLabel,
} from '@material-ui/core';
import { addDays, addMonths } from 'date-fns';
import { FormikContextType } from 'formik';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import enUs from '../../../../lang/en-us';
import AsyncLoader from '../../../../lib/AsyncLoader';
import { CheckBoxField } from '../../../../lib/CheckBoxField';
import DatePickerInput from '../../../../lib/DatePicker';
import { fetchIFSCDetails } from '../../../../redux-store/actions/nct';
import { IsAllow } from '../../../../redux-store/types/portfolio';
import {
    checkForOnlyAlphabets,
    getDateString,
    isNumber,
    OTM_MAX_DATE,
} from '../../../../utils/constants';
import { maskAccountNo } from '../../../../utils/utilityFunctions';
import { ifscRegex } from '../../makeNct/CorrectionFolios/types';
import { ValidationUI } from '../../makeNct/MinorStatus/minor-status-edit-bank';
import { TabElementSkeleton } from '../ExploreMFs/mf-investment';
import { showHelperText, isError } from '../Switch/utils';
import { ExtraValidationsType, InitStateType } from './types';
import {
    accountTypes,
    eMandateEndDateDisable,
    eMandateStartDateDisable,
    IFSC_LENGTH,
    MIN_EMANDATE_MONTHS,
    otmFrequency,
    VALIDATED_IFSC,
    VALIDATED_IFSC_SHOW_POPUP,
    VALIDATION_IFSC_FAILED,
    VALIDATION_IFSC_PENDING,
} from './utils';

const { dbCommercial, common } = enUs;

export const initIFSC = {
    name: '',
    branch: '',
    city: '',
};

type BankDetailsPropsType = {
    formik: FormikContextType<InitStateType>;
    latestDataCalled: boolean | null;
    extraErrors: ExtraValidationsType | null;
    setExtraErrors: Dispatch<SetStateAction<ExtraValidationsType | null>>;
};

export default function BankDetails({
    formik,
    latestDataCalled,
    extraErrors,
    setExtraErrors,
}: BankDetailsPropsType) {
    const dipatch = useDispatch();

    useEffect(() => {
        formik.setFieldValue(
            'toDate',
            addMonths(new Date(formik.values.fromDate), MIN_EMANDATE_MONTHS),
        );
    }, [formik.values.fromDate]);

    const _getIFSCDetails = async (ifsc: string = '') => {
        formik.setFieldValue('ifsc', ifsc.toUpperCase());
        // const ifsc = formik.values.bankName.ifscPrefixCode + _ifsc;
        if (!ifsc.match(ifscRegex)) {
            return;
        }
        try {
            formik.setStatus(VALIDATION_IFSC_PENDING);
            let response: any = await dipatch(
                fetchIFSCDetails({
                    ifscCode: ifsc.toUpperCase(),
                }),
            );
            const { errors = [], data = [] } = (response || {}).body;
            if (errors.length) {
                const [firstError] = errors;
                throw Error(firstError?.message);
            }
            const [details] = data;
            const { BankAdd1 = '', BankAdd2 = '', BankAdd3 = '' } = details;
            formik.setStatus(VALIDATED_IFSC_SHOW_POPUP);
            // formik.setFieldValue('ifscInfo', {
            //     ...initIFSC,
            //     name: details.BankName,
            //     branch: BankAdd1 || BankAdd2 || BankAdd3,
            //     city: details.BankCity,
            // });
            formik.setFieldValue('branch', BankAdd1 || BankAdd2 || BankAdd3);
            // formik.setFieldValue('name', details.BankName);
            formik.setFieldValue('city', details.BankCity);
            formik.setFieldValue('micr', details.MicrNo);
            onProceed(); // without dialog
        } catch (e: any) {
            formik.setStatus(VALIDATION_IFSC_FAILED);
            formik.setFieldError('ifsc', 'Ifsc validation failed');
        }
    };

    const onProceed = () => {
        formik.setStatus(VALIDATED_IFSC);
    };

    const isErrorPresent = (): boolean => {
        return !!(
            (formik.errors.bankName && formik.touched.bankName) ||
            (extraErrors && extraErrors.bankName && extraErrors.bankName.bankName)
        );
    };

    return (
        <>
            <h5>{dbCommercial.exploreMfs.eMandate.enterBankDetails}</h5>

            <div className="new-otm-fields">
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={200} />}
                    data={latestDataCalled}
                >
                    <>
                        <FormControl variant="outlined">
                            <TextField
                                id="investor-ifsc"
                                label={dbCommercial.exploreMfs.eMandate.fields.ifscCode}
                                variant="outlined"
                                value={formik.values.ifsc}
                                helperText={showHelperText(formik, 'ifsc')}
                                error={isError(formik, 'ifsc')}
                                onChange={async (e) => {
                                    if (e.target.value.length <= IFSC_LENGTH) {
                                        formik.handleChange(e);
                                        _getIFSCDetails(e.target.value);
                                    }
                                }}
                                onBlur={formik.handleBlur}
                                name="ifsc"
                            // InputProps={{
                            //     startAdornment: (
                            //         <InputAdornment position="start">
                            //             {formik.values.bankName.ifscPrefixCode}
                            //         </InputAdornment>
                            //     ),
                            // }}
                            />
                            {[VALIDATION_IFSC_PENDING, VALIDATED_IFSC].includes(formik.status) && (
                                <FormHelperText>
                                    <ValidationUI
                                        isValidating={formik.status === VALIDATION_IFSC_PENDING}
                                        onValidationText={common.validatingIFSC}
                                        successfulValidationText={common.IFSCValidated}
                                    />
                                </FormHelperText>
                            )}
                        </FormControl>
                        <TextField
                            id="investor-branch"
                            label={dbCommercial.exploreMfs.eMandate.fields.branchName}
                            variant="outlined"
                            helperText={showHelperText(formik, 'branch')}
                            error={isError(formik, 'branch')}
                            value={formik.values.branch}
                            onChange={(e) => {
                                if (checkForOnlyAlphabets(e.target.value)) {
                                    formik.setFieldValue('branch', e.target.value);
                                }
                            }}
                            onBlur={formik.handleBlur}
                            name="branch"
                            disabled={true}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </>
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={200} />}
                    data={latestDataCalled}
                >
                    <>
                        <TextField
                            id="investor-acHolder"
                            label={dbCommercial.exploreMfs.eMandate.fields.accountHolder}
                            variant="outlined"
                            helperText={showHelperText(formik, 'accountHolder')}
                            error={isError(formik, 'accountHolder')}
                            value={formik.values.accountHolder}
                            onChange={(e) => {
                                if (checkForOnlyAlphabets(e.target.value)) {
                                    formik.setFieldValue('accountHolder', e.target.value);
                                }
                            }}
                            onBlur={formik.handleBlur}
                            name="accountHolder"
                        />
                        <FormControl variant="outlined" error={isErrorPresent()}>
                            <InputLabel>{dbCommercial.exploreMfs.eMandate.fields.bank}</InputLabel>
                            <Select
                                onBlur={formik.handleBlur}
                                onChange={(e) => {
                                    const selectedBank = formik.values.allowedBanks.find(
                                        (_bank) => _bank.ifscPrefixCode === e.target.value,
                                    );
                                    formik.setFieldValue('bankName', selectedBank);
                                    setExtraErrors((prev) => ({ ...prev, bankName: null }));
                                    // _getIFSCDetails(formik.values.ifsc);
                                }}
                                name="bankName"
                                value={formik.values.bankName.ifscPrefixCode}
                                native={false}
                            >
                                {formik.values.allowedBanks.map(({ bankName, ifscPrefixCode }) => (
                                    <MenuItem key={ifscPrefixCode} value={ifscPrefixCode}>
                                        {bankName}
                                    </MenuItem>
                                ))}
                            </Select>
                            {!!(formik.errors.bankName && formik.touched.bankName) ? (
                                <FormHelperText
                                    style={{
                                        color: 'red',
                                        fontSize: 12,
                                        textAlign: 'left',
                                    }}
                                >
                                    {typeof formik.errors.bankName === 'string'
                                        ? formik.errors.bankName
                                        : formik.errors.bankName.bankName}
                                </FormHelperText>
                            ) : !!(
                                extraErrors &&
                                extraErrors.bankName &&
                                extraErrors.bankName.bankName
                            ) ? (
                                <FormHelperText
                                    style={{
                                        color: 'red',
                                        fontSize: 12,
                                        textAlign: 'left',
                                    }}
                                >
                                    {extraErrors.bankName.bankName}
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={200} />}
                    data={latestDataCalled}
                >
                    <TextField
                        id="bank-account"
                        label={dbCommercial.exploreMfs.eMandate.fields.bankAccountNo}
                        variant="outlined"
                        helperText={showHelperText(formik, 'accountNo')}
                        error={isError(formik, 'accountNo')}
                        value={formik.values.accountNo}
                        inputProps={{ inputMode: 'numeric' }}
                        onChange={(e) => {
                            // setExtraErrors((prev) => ({ ...(prev || {}), accountNoHint: '' }));
                            if (isNumber(e.target.value)) {
                                formik.setFieldValue('accountNo', e.target.value);
                            }
                        }}
                        onCopy={(e) => {
                            e.preventDefault();
                        }}
                        onPaste={(e) => {
                            e.preventDefault();
                        }}
                        type="password"
                        onBlur={formik.handleBlur}
                        name="accountNo"
                        InputProps={{
                            autoComplete: 'off',
                        }}
                    />
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={300} />}
                    data={latestDataCalled}
                >
                    <TextField
                        id="confirm-bank-account"
                        label={dbCommercial.exploreMfs.eMandate.fields.confirmBankAccountNo}
                        inputProps={{ inputMode: 'numeric' }}
                        variant="outlined"
                        value={formik.values.confirmAccountNumber}
                        autoComplete="off"
                        helperText={showHelperText(formik, 'confirmAccountNumber')}
                        error={isError(formik, 'confirmAccountNumber')}
                        onChange={(e) => {
                            if (isNumber(e.target.value)) {
                                formik.setFieldValue('confirmAccountNumber', e.target.value);
                            }
                        }}
                        onCopy={(e) => {
                            e.preventDefault();
                        }}
                        onPaste={(e) => {
                            e.preventDefault();
                        }}
                        onBlur={formik.handleBlur}
                        name="confirmAccountNumber"
                    />
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={200} />}
                    data={latestDataCalled}
                >
                    <TextField
                        id="investor-amount"
                        label={dbCommercial.exploreMfs.eMandate.fields.amount}
                        variant="outlined"
                        inputProps={{ inputMode: 'numeric' }}
                        value={formik.values.amount}
                        helperText={showHelperText(formik, 'amount')}
                        error={isError(formik, 'amount')}
                        onChange={(e) => {
                            if (isNumber(e.target.value)) {
                                formik.setFieldValue('amount', e.target.value);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        name="amount"
                    />
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={200} />}
                    data={latestDataCalled}
                >
                    <FormControl variant="outlined">
                        <InputLabel>
                            {dbCommercial.exploreMfs.eMandate.fields.accountType}
                        </InputLabel>
                        <Select
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={formik.values.accountType}
                        >
                            <MenuItem value={accountTypes.value}>{accountTypes.label}</MenuItem>
                        </Select>
                    </FormControl>
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={200} />}
                    data={latestDataCalled}
                >
                    <FormControl variant="outlined">
                        <InputLabel>{dbCommercial.exploreMfs.eMandate.fields.frequency}</InputLabel>
                        <Select value={formik.values.frequency}>
                            <MenuItem value={otmFrequency.value}>{otmFrequency.label}</MenuItem>
                        </Select>
                    </FormControl>
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={200} />}
                    data={latestDataCalled}
                >
                    <DatePickerInput
                        label={dbCommercial.exploreMfs.eMandate.fields.fromDate}
                        value={formik.values.fromDate}
                        onBlur={formik.handleBlur}
                        onChange={(fromDate) => {
                            formik.setFieldValue('fromDate', fromDate || addDays(new Date(), 1));
                        }}
                        shouldDisableDate={(date) => !!date && eMandateStartDateDisable(date)}
                        disableFuture={false}
                        disablePast={true}
                    />
                </AsyncLoader>
                <AsyncLoader
                    skelton={<TabElementSkeleton height={35} width={300} />}
                    data={latestDataCalled}
                >
                    <div className="new-otm-field-with-checkbox">
                        <DatePickerInput
                            label={dbCommercial.exploreMfs.eMandate.fields.toDate}
                            value={
                                formik.values.untilCancelled === IsAllow.YES
                                    ? OTM_MAX_DATE(formik.values.fromDate) 
                                    : formik.values.toDate
                            }
                            onChange={(toDate) => {
                                formik.setFieldValue('toDate', toDate || new Date());
                            }}
                            onBlur={formik.handleBlur}
                            disablePast={true}
                            disableFuture={false}
                            shouldDisableDate={(day) =>
                                getDateString(day as any) === getDateString(formik.values.fromDate) || eMandateEndDateDisable(day as any, formik.values.fromDate)
                            }
                            disabled={formik.values.untilCancelled === IsAllow.YES}
                        />
                        <CheckBoxField
                            onChange={(e) =>
                                formik.setFieldValue(
                                    'untilCancelled',
                                    e.target.checked === true ? IsAllow.YES : IsAllow.No,
                                )
                            }
                            value={formik.values.untilCancelled === IsAllow.YES}
                            label="Until cancelled(max 40 years)"
                        />
                    </div>
                </AsyncLoader>
            </div>
        </>
    );
}
